import { useMutation, useQuery } from '~/shared/hooks/query'
import { useQueryClient } from '@tanstack/react-query'
import { setDueDateToEOD } from '~/shared/lib/date'
import { useMemo } from 'react'
import keyBy from 'lodash/keyBy'
import {
  providersWorkUnits,
  ProvidersWorkUnitsPartialUpdateBodyParams,
  ProvidersWorkUnitsPartialUpdatePathParams,
  providersWorkUnitsWorklists,
  ProvidersWorkUnitsWorklistsListQueryParams,
  ProvidersWorkUnitsWorklistsListResponse,
  worklists,
} from '@fireflyhealth/api'
import invariant from 'tiny-invariant'
import type { CaseWorkUnit } from '~/entities/work-unit'

const getWorkLists = async () => {
  const { data } = await worklists.list()
  return data
}

export const listWorkListKey = 'listWorkList'
export const useWorkLists = () => {
  const { data, isPending: isLoading } = useQuery(
    [listWorkListKey],
    () => getWorkLists(),
    {},
    {
      error: 'Failed to fetch worklists',
    },
  )
  return { result: data, isLoading }
}

export const useWorkListsById = () => {
  const { result: workLists, isLoading } = useWorkLists()
  const result = useMemo(() => {
    return keyBy(workLists, workList => workList.id)
  }, [workLists])

  return { result, isLoading }
}

// Worklists only work with cases, despite ostensibly working with any WorkUnit content type.
function assertCase(
  workUnit: ProvidersWorkUnitsWorklistsListResponse['results'][number],
): asserts workUnit is CaseWorkUnit {
  invariant(
    workUnit.content_object.content_type === 'case',
    'Expected work unit content object to be a case',
  )
}

const getWorkUnitItems = async (
  worklistId: number | undefined,
  options: ProvidersWorkUnitsWorklistsListQueryParams,
) => {
  if (worklistId) {
    const { data } = await providersWorkUnitsWorklists.list(
      {
        provider_id: 'me',
        worklist_id: worklistId,
      },
      { params: options },
    )

    const workUnitData = data.results
    const workUnits = workUnitData.map(workUnit => {
      assertCase(workUnit)

      return {
        ...workUnit,
        worklist: worklistId,
        id: workUnit.content_object.id,
        pk: workUnit.content_object.id,
      }
    })
    return {
      items: workUnits,
      count: data.count || 0,
    }
  }
}

const getWorkUnitItemsKey = 'getWorkUnitItems'
export const useWorkUnitItems = (
  id: number | undefined,
  options: ProvidersWorkUnitsWorklistsListQueryParams,
) => {
  const { data, isPending: isLoading } = useQuery(
    [getWorkUnitItemsKey, id, options],
    () => getWorkUnitItems(id, options),
    { enabled: !!id },
    {
      error: 'Failed to fetch worklists',
    },
  )
  return { result: data, isLoading }
}

const updateWorkUnitItem = (
  workUnit: Omit<ProvidersWorkUnitsPartialUpdatePathParams, 'provider_id'> &
    ProvidersWorkUnitsPartialUpdateBodyParams,
) => {
  return providersWorkUnits.partialUpdate(
    {
      provider_id: 'me',
      content_type: workUnit.content_type,
      object_id: workUnit.object_id,
    },
    {
      data: {
        due_date: workUnit.due_date ? setDueDateToEOD(workUnit.due_date) : undefined,
        owner_group: workUnit.owner_group,
        action: workUnit.action,
      },
    },
  )
}

export const useUpdateWorkUnitItem = () => {
  const client = useQueryClient()

  return useMutation(
    updateWorkUnitItem,
    {
      onSuccess: async () => {
        await client.cancelQueries({ queryKey: [getWorkUnitItemsKey] })
        await client.invalidateQueries({ queryKey: [getWorkUnitItemsKey] })
      },
    },
    { error: 'Failed to update work unit' },
  )
}
